import React, {
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { selectors } from '../../../store/CurrentUser.model';
import { RootState } from '../../../store/models';
import {
  useParams,
  useOnce,
  useLinkTo,
} from '../../../hooks';
import { ProNavBar } from './ProNavBar';
import { shouldShowClientTabBadge } from '../notifications/AddClientNotification/AddClientNotificationModule';
import analytics from '../../../modules/analytics';
import AutoCheckoutSnackBar from '../../../modules/provider/AutoCheckoutSnackBar';
import PaymentsSwipers from '../../../modules/provider/PaymentsSwipers';
import ScheduleViewSaver from '../../../modules/provider/ScheduleViewSaver';
import { CALENDAR_ROUTE, PRO_CLIENT_LIST } from '../../../route-names';

export type TabOption = 'daily' | 'settings' | 'clients' | 'growth' | 'schedule' | 'marketing';

export type ProNavBarContainerProps = {
  tab?: TabOption;
};

const ProNavBarContainer = ({
  tab,
}: ProNavBarContainerProps) => {
  const linkTo = useLinkTo();
  const {
    providerId: providerIdParam,
    proId,
    proTab,
  } = useParams() || {};
  const currentRoute = useSelector<RootState, string>(
    state => state?.route?.name,
  );

  // Route params take precedence for initial tab value
  const [
    selectedTab,
    setSelectedTab,
  ] = useState<TabOption>(
    proTab as TabOption || tab,
  );
  const [
    showClientTabBadge,
    setShowClientTabBadge,
  ] = useState<boolean>(false);
  const providerIdFromUser = useSelector<RootState, number>(
    selectors.getProviderId,
  );
  const providerId = useMemo(() => {
    const providerIdFromParams = providerIdParam || proId;
    if (Number.isNaN(Number(providerIdFromParams))) {
      return providerIdFromUser;
    }
    return Number(providerIdFromParams);
  }, [
    providerIdParam,
    proId,
    providerIdFromUser,
  ]);

  // Initialize the component
  useOnce(async () => {
    PaymentsSwipers.showSnackBars();
    AutoCheckoutSnackBar.showSnackBars(providerId);
    const shouldShowBadge = await shouldShowClientTabBadge(providerId);
    if (shouldShowBadge) {
      analytics.track('pro_client_badge_viewed', { providerId });
    }
    setShowClientTabBadge(shouldShowBadge);
  }, providerId || null);

  const navigateTo = useCallback((
    stateName: string,
    stateParams?: Record<string, string | number | undefined | null>,
    options: {
      notify?: boolean;
      reload?: boolean;
      replace?: boolean;
    } = {},
  ) => {
    const mergedParams = {
      proTab: undefined,
      providerId,
      ...stateParams,
    };
    const {
      notify,
      reload,
      replace,
    } = options;

    linkTo(
      stateName,
      mergedParams,
      notify,
      reload,
      replace,
    );
  }, [
    linkTo,
    providerId,
  ]);

  const goToMarketing = useCallback(() => {
    analytics.track('pro_nav_bar_marketinghub_click');
    setSelectedTab('marketing');
    navigateTo('provider.marketing_hub');
  }, [
    setSelectedTab,
    navigateTo,
  ]);

  const goToGrowth = useCallback(() => {
    setSelectedTab('growth');
    navigateTo('provider.growth_tab.summary');
  }, [
    setSelectedTab,
    navigateTo,
  ]);

  const goToSettings = useCallback(() => {
    setSelectedTab('settings');
    navigateTo('provider.settings_menu.more', { source: 'core_navigation_more' });
  }, [
    setSelectedTab,
    navigateTo,
  ]);

  const goToClients = useCallback(() => {
    setSelectedTab('clients');
    navigateTo(
      PRO_CLIENT_LIST,
      { source: 'core_navigation_more' },
      {
        reload: currentRoute === PRO_CLIENT_LIST,
      },

    );
  }, [
    setSelectedTab,
    navigateTo,
    currentRoute,
  ]);

  const goToSchedule = useCallback(() => {
    const savedView = ScheduleViewSaver.getSavedView();
    const onCalendar = currentRoute.includes(CALENDAR_ROUTE);

    setSelectedTab('schedule');

    const today = moment();
    const dateParams = {
      year: today.year(),
      month: today.month() + 1,
      day: today.date(),
      ...(onCalendar ? {} : savedView?.dateParams),
    };

    navigateTo(CALENDAR_ROUTE, {
      ...dateParams,
      source: 'core_navigation_more',
    });
  }, [
    setSelectedTab,
    navigateTo,
    currentRoute,
  ]);

  const goToToday = useCallback(() => {
    // Scroll to now, if we're already on the today tab
    document.dispatchEvent(new CustomEvent('pro-schedule-timeline:scroll-to-now'));
    setSelectedTab('daily');
    navigateTo(
      'provider.todayNext',
      {
        source: 'core_navigation_more',
        view: 'timeline',
      },
    );
  }, [
    setSelectedTab,
    navigateTo,
  ]);

  return (
    <ProNavBar
      providerId={`${providerId}`}
      selectedTab={selectedTab}
      showClientTabBadge={showClientTabBadge}
      onGoToMarketing={goToMarketing}
      onGoToGrowth={goToGrowth}
      onGoToSchedule={goToSchedule}
      onGoToToday={goToToday}
      onGoToSettings={goToSettings}
      onGoToClients={goToClients}
    />
  );
};

export default ProNavBarContainer;
